import React, { useEffect, useState } from 'react';
import { CircleFlag } from 'react-circle-flags';
import { Row, Typography, Checkbox, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding } from '@fortawesome/free-regular-svg-icons';
import { faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import { calculeValuesPerGroup } from '../../../../../services/utils';
import { getCpmByStatusCampaign } from '../../../../../services/display';
import { distribution } from '../../services/investmentCalculations';
import ScreensCollectionHolder from '../../styles/screensCollectionStyle';
import InvestmentManagerComponent from './InvestmentManagerComponent';
import ScreensGroupContainer from '../../containers/step2_settings/ScreensGroupContainer';

const { Title } = Typography;

const ScreensCollectionComponent = props => {

  const { t } = useTranslation();
  const [displaysCollections, setDisplaysCollections] = useState([]);
  const {
    cart,
    managerDisplaySelector,
    disabledManagerDisplayFilter,
    applyRelationOnScreens,
    allDisplaysAreOwn,
    programmatic,
    customContents,
    currency,
    audience,
    isPaymentStatusApproved,
    editCampaignActive,
    currentCampaign,
    campaignCurrency,
    updateCustomContents,
    enabledRenderRules,
    realAmount,
    isSuperUser,
    toggleSecuredInvestment
  } = props;

  const getFlag = collection => {
    const firstDisplay = collection.displays[0];
    const countryAbbreviation =
      firstDisplay && firstDisplay.country_iso && firstDisplay.country_iso.toLowerCase();
    return countryAbbreviation;
  };

  const groupByCountry = () => {
    let result = Object.values(
      cart.reduce((acc, display) => {
        const country = display.country || 'Not assigned';
        const value = parseFloat(display.smart_campaign_cpm / 1000);
        const maxCpm = (Math.floor(value * 100) + 1) / 100 || 0;
        if (!acc[country]) {
          acc[country] = {
            key: country,
            displays: [],
            maxCpm: maxCpm
          };
        }
        acc[country].displays.push(display);
        acc[country].maxCpm = Math.max(acc[country].maxCpm, maxCpm);
        return acc;
      }, {})
    );
    if (programmatic === 'programmatic') {
      result = genereInvestmentGroup(result);
    }
    return result;
  };

  const groupByGeoField = field => {
    let result = Object.values(
      cart.reduce((acc, display) => {
        const value = display[field] || 'Not assigned';
        const cpm = parseFloat(display.smart_campaign_cpm / 1000);
        const maxCpm = (Math.floor(cpm * 100) + 1) / 100 || 0;
        if (!acc[value]) {
          acc[value] = { key: value, displays: [], maxCpm };
        }
        acc[value].displays.push(display);
        acc[value].maxCpm = Math.max(acc[value].maxCpm, maxCpm);
        return acc;
      }, {})
    );
    if (programmatic === 'programmatic') result = genereInvestmentGroup(result);
    return result;
  };

  const groupByCompany = () => {
    let result = Object.values(
      cart.reduce((acc, display) => {
        const company_id = display.company_id || 'Not assigned';
        const value = parseFloat(display.smart_campaign_cpm / 1000);
        const maxCpm = (Math.floor(value * 100) + 1) / 100 || 0;
        if (!acc[company_id]) {
          acc[company_id] = {
            key: display.company.name,
            displays: [],
            maxCpm: maxCpm
          };
        }
        acc[company_id].displays.push(display);
        acc[company_id].maxCpm = Math.max(acc[company_id].maxCpm, maxCpm);
        return acc;
      }, {})
    );
    if (programmatic === 'programmatic') {
      result = genereInvestmentGroup(result);
    }
    return result;
  };

  const genereInvestmentGroup = result => {
    result = result.map(group => {
      let investmentGroup;
      const option = editCampaignActive
        ? { currentCampaign: currentCampaign, calculateImpacts: false, editCampaignActive }
        : { calculateImpacts: false, editCampaignActive };
      [investmentGroup] = calculeValuesPerGroup(group, customContents, audience, option);
      return { ...group, investmentGroup };
    });
    return result;
  };

  const handleChangeGroupProposal = (
    newValue, // New value of the group investment
    groupDisplays, // Array of group displays
    prevValue, // Previous value of the group investment
    typeOfUpdate = 'default', // 'default' or 'percentage'
    parentGroupInvestment, // Parent group investment
    sibblingDisplays
  ) => {
    if (newValue == prevValue) return;
    const newCustomContent = distribution.byGroup(
      newValue,
      groupDisplays,
      customContents,
      sibblingDisplays,
      parentGroupInvestment,
      {
        getCpm: display => getCpmByStatusCampaign(editCampaignActive, currentCampaign, display),
        typeOfUpdate
      }
    );
    updateCustomContents(newCustomContent);
  };

  const groupAccordingToFilter = () => {
    let displaysGroupAux;
    if (managerDisplaySelector === 'country') displaysGroupAux = groupByCountry();
    if (managerDisplaySelector === 'company') displaysGroupAux = groupByCompany();
    if (managerDisplaySelector === 'administrative_area_level_1') displaysGroupAux = groupByGeoField('administrative_area_level_1');
    if (managerDisplaySelector === 'administrative_area_level_2') displaysGroupAux = groupByGeoField('administrative_area_level_2');
    setDisplaysCollections(displaysGroupAux);
  };

  useEffect(() => {
    if (!managerDisplaySelector) return;
    groupAccordingToFilter();
  }, [cart, managerDisplaySelector, customContents]);

  return (
    <ScreensCollectionHolder>
      {managerDisplaySelector && !disabledManagerDisplayFilter ? (
        <Row type="flex" className="Screens-collection">
          {displaysCollections.map((collection, index) => {
            let contentsDisplay = customContents.filter(item =>
              collection.displays.some(display => display.id === item.displayId)
            );
            const isCollectionSecured =
              contentsDisplay.length > 0 && contentsDisplay.every(c => c.is_secured_investment);
            const companyId =
              collection.displays.length > 0 ? collection.displays[0].company_id : null;
            const spotValue =
              Math.floor(
                parseFloat(collection.investmentGroup ? collection.investmentGroup : 0) * 100
              ) / 100 || 0;
            const isFixedBudgetDisplay = contentsDisplay.every(item => item.isFixedBudget === true);
            return (
              <Row key={index} className="collection" type="flex">
                <Row
                  type="flex"
                  className="title-collection appear-animation-without-movement"
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Title level={3} className="margin-text">
                      {t(collection.key)}
                    </Title>
                    {managerDisplaySelector === 'country' ? (
                      <CircleFlag
                        className="margin-text"
                        countryCode={getFlag(collection)}
                        height="24"
                        width="24"
                      />
                    ) : managerDisplaySelector === 'company' ? (
                      <FontAwesomeIcon
                        className="iconCompany"
                        icon={faBuilding}
                        style={{ fontSize: '20px' }}
                      />
                    ) : (
                      <FontAwesomeIcon
                        className="iconCompany"
                        icon={faMapMarkerAlt}
                        style={{ fontSize: '20px' }}
                      />
                    )}
                    <Title level={3} className="margin-text">
                      {`(${collection.displays.length || 0})`}
                    </Title>
                    {managerDisplaySelector === 'company' && programmatic === 'programmatic' && (
                      isSuperUser ? (
                        <Tooltip title={t('This medium works with secured investment.')}>
                          <span
                            style={{ display: 'flex', alignItems: 'center', gap: '4px', marginLeft: '8px' }}
                          >
                            <Checkbox
                              checked={isCollectionSecured}
                              onChange={e => toggleSecuredInvestment(companyId, e.target.checked)}
                            />
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 65.5 87.97" style={{ width: '20px', height: '20px' }}>
                              <path fill="#979696" d="M59.7,31.94c0-14.42-11.69-26.11-26.11-26.11S7.48,17.52,7.48,31.94c0,8.37,3.94,15.81,10.06,20.59l-7.69,24.13,11.65-5.25,7.93,9.27,4.64-21.81,4.64,21.81,8.09-9.12,11.5,5.1-7.88-24.74c5.68-4.79,9.3-11.96,9.3-19.97ZM12.77,31.94c0-11.5,9.32-20.82,20.82-20.82s20.82,9.32,20.82,20.82-9.32,20.82-20.82,20.82-20.82-9.32-20.82-20.82Z"/>
                              <circle fill="#979696" cx="33.59" cy="31.94" r="12.81"/>
                            </svg>
                          </span>
                        </Tooltip>
                      ) : isCollectionSecured ? (
                        <Tooltip title={t('This medium works with secured investment.')}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 65.5 87.97" style={{ width: '20px', height: '20px', marginLeft: '8px' }}>
                            <path fill="#979696" d="M59.7,31.94c0-14.42-11.69-26.11-26.11-26.11S7.48,17.52,7.48,31.94c0,8.37,3.94,15.81,10.06,20.59l-7.69,24.13,11.65-5.25,7.93,9.27,4.64-21.81,4.64,21.81,8.09-9.12,11.5,5.1-7.88-24.74c5.68-4.79,9.3-11.96,9.3-19.97ZM12.77,31.94c0-11.5,9.32-20.82,20.82-20.82s20.82,9.32,20.82,20.82-9.32,20.82-20.82,20.82-20.82-9.32-20.82-20.82Z"/>
                            <circle fill="#979696" cx="33.59" cy="31.94" r="12.81"/>
                          </svg>
                        </Tooltip>
                      ) : null
                    )}
                  </div>
                  {programmatic === 'programmatic' &&
                    (managerDisplaySelector === 'company' ||
                      managerDisplaySelector === 'country' ||
                      managerDisplaySelector === 'administrative_area_level_1' ||
                      managerDisplaySelector === 'administrative_area_level_2') && (
                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <span
                          className="textGroup margin-text"
                          style={{ alignContent: 'center', marginBottom: '5px' }}
                        >
                          {t('Investment')}
                        </span>
                        <div className="proposalGroup" style={{ marginLeft: 3 }}>
                          <InvestmentManagerComponent
                            currency={editCampaignActive ? campaignCurrency : currency}
                            value={spotValue}
                            parentGroupInvestment={realAmount}
                            onChange={(value, typeOfUpdate) => {
                              handleChangeGroupProposal(
                                value,
                                collection.displays,
                                collection.investmentGroup,
                                typeOfUpdate,
                                realAmount,
                                displaysCollections
                                  .filter(group =>
                                    group.displays.every(
                                      display => display.id !== collection.displays[0].id
                                    )
                                  )
                                  .flatMap(group => group.displays)
                              );
                            }}
                            disabled={isPaymentStatusApproved || isFixedBudgetDisplay}
                            step={collection.maxCpm * collection.displays.length + 1}
                            tooltip={t(
                              'This indicates what percentage of the campaign’s total budget corresponds to the investment assigned to this group. You can modify it by editing either the investment or directly the percentage.'
                            )}
                          />
                        </div>
                      </div>
                    )}
                </Row>
                <ScreensGroupContainer
                  displaysCollection={collection.displays}
                  applyRelationOnScreens={applyRelationOnScreens}
                  allDisplaysAreOwn={allDisplaysAreOwn}
                  isPaymentStatusApproved={isPaymentStatusApproved}
                  enabledRenderRules={enabledRenderRules}
                  parentGroupInvestment={spotValue}
                  handleChangeGroupProposal={handleChangeGroupProposal}
                />
              </Row>
            );
          })}
        </Row>
      ) : (
        <ScreensGroupContainer
          displaysCollection={cart}
          applyRelationOnScreens={applyRelationOnScreens}
          allDisplaysAreOwn={allDisplaysAreOwn}
          isPaymentStatusApproved={isPaymentStatusApproved}
          enabledRenderRules={enabledRenderRules}
          parentGroupInvestment={realAmount}
          handleChangeGroupProposal={handleChangeGroupProposal}
        />
      )}
    </ScreensCollectionHolder>
  );
};

export default ScreensCollectionComponent;
